/**
 * Normie Portrait renderer — renders a Normie's pixel art as full-background canvas art.
 *
 * Async renderer (like strata/meridian) because it fetches an external image.
 *
 * Signature: renderNormiePortraitAsync(ctx, W, H, palette, params, seed)
 *   palette — { bg, colors, accent, renderMode? } (already adjusted)
 *   params  — { tokenId, zoom }
 *   seed    — integer seed (unused for image fetch, used for minor visual variations)
 *
 * Palette renderMode determines the color treatment:
 *   'original'  — draw the Normie image with original pixel colors
 *   'invert'    — invert all RGB channels
 *   'colorize'  — map grayscale luminance to a palette color gradient
 *   (default)   — draw as-is (same as original)
 */

const NORMIES_API = 'https://api.normies.art';

// ── Image cache ──
const imageCache = new Map();

function loadNormieImage(tokenId) {
  const url = `${NORMIES_API}/normie/${tokenId}/image.png`;
  if (imageCache.has(url)) return imageCache.get(url);

  const promise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageCache.set(url, Promise.resolve(img));
      resolve(img);
    };
    img.onerror = () => {
      imageCache.delete(url);
      reject(new Error(`Failed to load Normie #${tokenId}`));
    };
    img.src = url;
  });

  imageCache.set(url, promise);
  return promise;
}

// ── Color utilities ──

function hexToRgb(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function luminance(r, g, b) {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * Build a color gradient from palette.colors for luminance mapping.
 * Returns an array of 256 [r,g,b] values (one per luminance level).
 */
function buildGradientLUT(paletteColors) {
  const rgbStops = paletteColors.map(hexToRgb);
  const lut = new Array(256);
  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    const segmentFloat = t * (rgbStops.length - 1);
    const segIdx = Math.min(Math.floor(segmentFloat), rgbStops.length - 2);
    const localT = segmentFloat - segIdx;
    const a = rgbStops[segIdx];
    const b = rgbStops[segIdx + 1];
    lut[i] = [
      Math.round(a[0] + (b[0] - a[0]) * localT),
      Math.round(a[1] + (b[1] - a[1]) * localT),
      Math.round(a[2] + (b[2] - a[2]) * localT),
    ];
  }
  return lut;
}

// ── Main async renderer ──

export async function renderNormiePortraitAsync(ctx, W, H, palette, params, seed) {
  const tokenId = (params && params.tokenId) || 42;
  const renderMode = palette.renderMode || 'original';

  // Fill background
  ctx.fillStyle = palette.bg;
  ctx.fillRect(0, 0, W, H);

  let img;
  try {
    img = await loadNormieImage(tokenId);
  } catch (err) {
    // Draw a placeholder on failure
    ctx.fillStyle = palette.accent || '#48494b';
    ctx.font = 'bold 48px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`NORMIE #${tokenId}`, W / 2, H / 2 - 30);
    ctx.font = '28px Inter, sans-serif';
    ctx.fillText('Image unavailable', W / 2, H / 2 + 30);
    return;
  }

  // ── Draw image scaled to fill canvas ──
  // Calculate dimensions to cover the canvas while maintaining aspect ratio
  const imgAspect = img.width / img.height;
  const canvasAspect = W / H;
  let drawW, drawH;
  if (imgAspect > canvasAspect) {
    drawH = H;
    drawW = drawH * imgAspect;
  } else {
    drawW = W;
    drawH = drawW / imgAspect;
  }
  const drawX = (W - drawW) / 2;
  const drawY = (H - drawH) / 2;

  // For pixel art: disable smoothing to keep crisp pixels
  ctx.imageSmoothingEnabled = false;

  if (renderMode === 'original') {
    // Draw with original colors — no pixel manipulation needed
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    return;
  }

  // For other modes: draw to a temp canvas, manipulate pixels, then composite
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = W;
  tempCanvas.height = H;
  const tempCtx = tempCanvas.getContext('2d');
  tempCtx.imageSmoothingEnabled = false;

  // Fill with bg first (for transparent areas)
  tempCtx.fillStyle = palette.bg;
  tempCtx.fillRect(0, 0, W, H);
  tempCtx.drawImage(img, drawX, drawY, drawW, drawH);

  const imageData = tempCtx.getImageData(0, 0, W, H);
  const data = imageData.data;

  if (renderMode === 'invert') {
    for (let i = 0; i < data.length; i += 4) {
      data[i]     = 255 - data[i];     // R
      data[i + 1] = 255 - data[i + 1]; // G
      data[i + 2] = 255 - data[i + 2]; // B
      // Alpha unchanged
    }
  } else if (renderMode === 'colorize') {
    // Map each pixel's luminance to palette gradient
    const lut = buildGradientLUT(palette.colors);
    for (let i = 0; i < data.length; i += 4) {
      const lum = luminance(data[i], data[i + 1], data[i + 2]);
      const idx = Math.min(255, Math.max(0, Math.round(lum * 255)));
      const [r, g, b] = lut[idx];
      data[i]     = r;
      data[i + 1] = g;
      data[i + 2] = b;
    }
  }

  tempCtx.putImageData(imageData, 0, 0);
  ctx.drawImage(tempCanvas, 0, 0);
}

/**
 * Synchronous fallback — draws a placeholder with palette-tinted bg.
 * Used by renderGenerator when async isn't available (thumbnails).
 */
export function renderNormiePortraitSync(ctx, W, H, palette, params) {
  const tokenId = (params && params.tokenId) || 42;
  ctx.fillStyle = palette.bg;
  ctx.fillRect(0, 0, W, H);

  // Draw a stylized placeholder
  const accent = palette.accent || '#48494b';
  const cx = W / 2;
  const cy = H / 2;
  const size = Math.min(W, H) * 0.4;

  // Outer frame
  ctx.strokeStyle = accent;
  ctx.lineWidth = 4;
  ctx.strokeRect(cx - size / 2, cy - size / 2, size, size);

  // Inner pixel grid pattern (hints at pixel art)
  const gridSize = Math.floor(size / 8);
  ctx.fillStyle = accent;
  ctx.globalAlpha = 0.3;
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      // Checkerboard with some variation based on position
      if ((row + col) % 2 === 0 || (row === 3 && col >= 2 && col <= 5)) {
        ctx.fillRect(
          cx - size / 2 + col * gridSize + 1,
          cy - size / 2 + row * gridSize + 1,
          gridSize - 2,
          gridSize - 2,
        );
      }
    }
  }
  ctx.globalAlpha = 1;

  // Token ID label
  ctx.fillStyle = accent;
  ctx.font = `bold ${Math.round(W * 0.035)}px "IBM Plex Mono", monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`#${tokenId}`, cx, cy + size / 2 + 30);
}
